import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Twitter, Send } from "lucide-react";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";

const DEFAULT_FOOTER = {
  tagline: "SİNE81 — Türkiye'nin 81 ilinden gerçeği yansıtan haber ve TV platformu.",
  copyright: "© {year} SİNE81 — Sine81.com — Tüm hakları saklıdır.",
  columns: [
    {
      title: "Kurumsal",
      links: [
        { label: "Hakkımızda", url: "/kurumsal/hakkimizda" },
        { label: "Künye", url: "/kurumsal/kunye" },
        { label: "Reklam", url: "/kurumsal/reklam" },
        { label: "İletişim", url: "/kurumsal/iletisim" },
        { label: "Yayın İlkeleri", url: "/kurumsal/yayin-ilkeleri" },
      ],
    },
    {
      title: "Yasal",
      links: [
        { label: "KVKK Aydınlatma Metni", url: "/kurumsal/kvkk" },
        { label: "Gizlilik Politikası", url: "/kurumsal/gizlilik" },
        { label: "Çerez Politikası", url: "/kurumsal/cerez" },
        { label: "Kullanım Şartları", url: "/kurumsal/kullanim-sartlari" },
      ],
    },
    {
      title: "Platform",
      links: [
        { label: "Anasayfa", url: "/" },
        { label: "Köşe Yazıları", url: "/kose-yazilari" },
        { label: "Video", url: "/video" },
        { label: "TV", url: "/tv" },
        { label: "Haber Hattı", url: "/haber-hatti" },
      ],
    },
  ],
};

export default function Footer() {
  const { selected } = useProvince();
  const [social, setSocial] = useState({});
  const [cfg, setCfg] = useState(DEFAULT_FOOTER);

  useEffect(() => {
    api
      .get(`/social-media${selected ? `?province_id=${selected.id}` : ""}`)
      .then((r) => setSocial(r.data || {}))
      .catch(() => {});
  }, [selected?.id]);

  useEffect(() => {
    api
      .get("/settings/footer_config")
      .then((r) => {
        const v = r.data?.value;
        if (v && typeof v === "object") {
          setCfg({
            tagline: v.tagline || DEFAULT_FOOTER.tagline,
            copyright: v.copyright || DEFAULT_FOOTER.copyright,
            columns:
              Array.isArray(v.columns) && v.columns.length
                ? v.columns
                : DEFAULT_FOOTER.columns,
          });
        }
      })
      .catch(() => {});
  }, []);

  const copy = (cfg.copyright || DEFAULT_FOOTER.copyright).replace(
    "{year}",
    String(new Date().getFullYear())
  );

  return (
    <footer className="bg-[#0b0b0d] text-white mt-6" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2 md:col-span-2">
          <div className="inline-block">
            <Logo size="lg" invert />
          </div>
          <p className="mt-3 text-sm text-gray-300 max-w-sm" data-testid="footer-tagline">
            {cfg.tagline}
          </p>
          <div className="flex gap-2 mt-4">
            {social.facebook && (
              <a href={social.facebook} target="_blank" rel="noreferrer" className="p-2 bg-white/10 hover:bg-[#e11d2a] rounded-full">
                <Facebook className="w-4 h-4" />
              </a>
            )}
            {social.instagram && (
              <a href={social.instagram} target="_blank" rel="noreferrer" className="p-2 bg-white/10 hover:bg-[#e11d2a] rounded-full">
                <Instagram className="w-4 h-4" />
              </a>
            )}
            {social.twitter && (
              <a href={social.twitter} target="_blank" rel="noreferrer" className="p-2 bg-white/10 hover:bg-[#e11d2a] rounded-full">
                <Twitter className="w-4 h-4" />
              </a>
            )}
            {social.youtube && (
              <a href={social.youtube} target="_blank" rel="noreferrer" className="p-2 bg-white/10 hover:bg-[#e11d2a] rounded-full">
                <Youtube className="w-4 h-4" />
              </a>
            )}
            {social.telegram && (
              <a href={social.telegram} target="_blank" rel="noreferrer" className="p-2 bg-white/10 hover:bg-[#e11d2a] rounded-full">
                <Send className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>

        {(cfg.columns || []).map((col, i) => (
          <FCol key={i} title={col.title}>
            {(col.links || []).map((l, j) => (
              <FLink key={j} to={l.url}>
                {l.label}
              </FLink>
            ))}
          </FCol>
        ))}
      </div>

      <div className="border-t border-white/10 py-4 text-center text-xs text-gray-400" data-testid="footer-copyright">
        {copy}
      </div>
    </footer>
  );
}

const FCol = ({ title, children }) => (
  <div>
    <div className="font-bold mb-3 text-sm uppercase tracking-wider text-white">
      {title}
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const FLink = ({ to, children }) => {
  // Support external links transparently.
  const external = /^https?:\/\//i.test(to || "");
  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noreferrer"
        className="block text-sm text-gray-300 hover:text-[#e11d2a]"
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={to || "/"} className="block text-sm text-gray-300 hover:text-[#e11d2a]">
      {children}
    </Link>
  );
};
